import { type FC } from "react";
import { ModalBackground, ModalClose, ModalHeader } from "@/components/modal/shared.ts";
import { styled } from "@linaria/react";
import { theme } from "@/themes/theme.ts";
import { Switch } from "@/components/Switch.tsx";
import { Column } from "@/components/common/column.ts";
import {
  SettingContent,
  SettingContentTooltip,
  useSettingsStore,
} from "@/stores/useSettingsStore.ts";
import { Row } from "@/components/common/row.ts";
import { IconX } from "@/icons/IconX.tsx";
import { Slider } from "@/components/inputs/Slider.tsx";

interface ModalSettingsMenuProps {
  onClose: () => void;
}

const ModalPage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: white;
  padding: 1rem;
  border-radius: ${theme.brandBorderRadius};
  min-width: 600px;
`;

const Collection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-content: center;
  gap: 10px;

  &[data-disabled="true"] {
    opacity: 0.5;
  }
`;

const LineIndent = styled.div`
  width: 25px;
  align-self: center;
  height: 10px;
  border-bottom: 2px solid ${theme.brandBorderColor};
  border-left: 2px solid ${theme.brandBorderColor};
  transform: translateY(-5px);
`;

const LineLeft = styled.div`
  width: 50px;
  align-self: center;
  border-bottom: 2px solid ${theme.brandBorderColor};
`;

const LineRight = styled.div`
  width: stretch;
  align-self: center;
  border-bottom: 2px solid ${theme.brandBorderColor};
`;

const HeaderText = styled.p`
  opacity: 0.6;
`;

export const ModalSettingsMenu: FC<ModalSettingsMenuProps> = ({ onClose }) => {
  const { settings, setSettings } = useSettingsStore();

  return (
    <ModalBackground>
      <ModalPage>
        <Row data-width="full" data-justify="between">
          <Column>
            <ModalHeader>Settings</ModalHeader>
            <HeaderText>Settings will be saved automatically</HeaderText>
          </Column>
          <ModalClose onClick={onClose}>
            <IconX />
          </ModalClose>
        </Row>

        <Row>
          <Column data-width="full">
            <Row data-width="full">
              <LineLeft />
              <p>Notifications</p>
              <LineRight />
            </Row>
            <Collection data-tooltip={SettingContentTooltip.AudioChatReply} data-direction={"left"}>
              <Switch
                state={settings.AudioChatBlip}
                callback={() => {
                  setSettings({
                    ...settings,
                    AudioChatBlip: !settings.AudioChatBlip,
                  });
                }}
              />
              <span>{SettingContent.AudioChatReply}</span>
            </Collection>
            {settings.AudioChatBlip && (
              <Collection
                data-tooltip={SettingContentTooltip.AudioChatAlways}
                data-direction={"left"}
              >
                <LineIndent />
                <Switch
                  state={settings.AudioChatAlways}
                  callback={() => {
                    setSettings({
                      ...settings,
                      AudioChatAlways: !settings.AudioChatAlways,
                    });
                  }}
                />
                <span>{SettingContent.AudioChatAlways}</span>
              </Collection>
            )}
            <Collection
              data-tooltip={SettingContentTooltip.AudioNewSession}
              data-direction={"left"}
            >
              <Switch
                state={settings.AudioNewSession}
                callback={() => {
                  setSettings({
                    ...settings,
                    AudioNewSession: !settings.AudioNewSession,
                  });
                }}
              />
              <span>{SettingContent.AudioNewSession}</span>
            </Collection>
          </Column>
          <br />

          <Column data-width="full">
            <Row data-width="full">
              <LineLeft />
              <p>Misc</p>
              <LineRight />
            </Row>{" "}
            <Collection data-tooltip={SettingContentTooltip.AdminEnabled} data-direction={"right"}>
              <Switch
                state={settings.AdminEnabled}
                callback={() => {
                  setSettings({
                    ...settings,
                    AdminEnabled: !settings.AdminEnabled,
                  });
                }}
              />
              <span>{SettingContent.AdminEnabled}</span>
            </Collection>
            <Collection
              data-tooltip={SettingContentTooltip.MiscSignalRDebug}
              data-direction={"right"}
            >
              <Switch
                state={settings.MiscSignalRDebug}
                callback={() => {
                  setSettings({
                    ...settings,
                    MiscSignalRDebug: !settings.MiscSignalRDebug,
                  });
                }}
              />
              <span>{SettingContent.MiscSignalRDebug}</span>
            </Collection>
            <Collection
              data-tooltip={SettingContentTooltip.MiscDisableTooltips}
              data-direction={"right"}
            >
              <Switch
                state={settings.MiscDisableTooltips}
                callback={() => {
                  setSettings({
                    ...settings,
                    MiscDisableTooltips: !settings.MiscDisableTooltips,
                  });
                }}
              />
              <span>{SettingContent.MiscDisableTooltips}</span>
            </Collection>
            {/*{settings.MiscRefreshTimer}s {SettingContent.MiscRefreshTimer}*/}
            {/*<Collection*/}
            {/*  data-tooltip={SettingContentTooltip.MiscRefreshTimer}*/}
            {/*  data-direction={"right"}*/}
            {/*>*/}
            {/*  <Row data-align="center" data-jufity={"center"} data-width={"full"}>*/}
            {/*    <Slider*/}
            {/*      id={"MiscRefreshTimer"}*/}
            {/*      state={settings.MiscRefreshTimer}*/}
            {/*      min={1}*/}
            {/*      max={30}*/}
            {/*      onChange={(value) => {*/}
            {/*        setSettings({*/}
            {/*          ...settings,*/}
            {/*          MiscRefreshTimer: value,*/}
            {/*        });*/}
            {/*      }}*/}
            {/*    />*/}
            {/*  </Row>*/}
            {/*</Collection>*/}
          </Column>
        </Row>
        <Row>
          <Column data-width="full">
            <Row data-width="full">
              <LineLeft />
              <p>Styling</p>
              <LineRight />
            </Row>
            <Collection
              data-tooltip={SettingContentTooltip.ChatContextPopout}
              data-direction={"left"}
            >
              <Switch
                state={settings.ChatContextPopout}
                callback={() => {
                  setSettings({
                    ...settings,
                    ChatContextPopout: !settings.ChatContextPopout,
                  });
                }}
              />
              <span>{SettingContent.ChatContextPopout}</span>
            </Collection>
            <Collection data-tooltip={SettingContentTooltip.SmallHeight} data-direction={"left"}>
              <Switch
                state={settings.SmallHeight}
                callback={() => {
                  setSettings({
                    ...settings,
                    SmallHeight: !settings.SmallHeight,
                  });
                }}
              />
              <span>{SettingContent.SmallHeight}</span>
            </Collection>
            <Row>
              {SettingContent.FontSizeOffset}: {settings.FontSizeOffset}
            </Row>
            <Collection data-tooltip={SettingContentTooltip.FontSizeOffset} data-direction={"left"}>
              <Row data-align="center" data-jufity={"center"} data-width={"full"}>
                <Slider
                  id={"MiscRefreshTimer"}
                  state={settings.FontSizeOffset}
                  min={-5}
                  step={0.25}
                  max={5}
                  fillFromMiddle={true}
                  onChange={(value) => {
                    setSettings({
                      ...settings,
                      FontSizeOffset: value,
                    });
                  }}
                />
              </Row>
            </Collection>
          </Column>
          <Column data-width="full">
            <Row data-width="full">
              <LineLeft />
              <p>Experimental</p>
              <LineRight />
            </Row>
            <Collection data-tooltip={SettingContentTooltip.MessagePack} data-direction={"right"}>
              <Switch
                state={settings.MessagePack}
                callback={() => {
                  setSettings({
                    ...settings,
                    MessagePack: !settings.MessagePack,
                  });
                }}
              />
              <span>{SettingContent.MessagePack}</span>
            </Collection>
          </Column>
        </Row>
      </ModalPage>
    </ModalBackground>
  );
};
